<template>
  <component
    :is="componentIs"
    v-bind="mergedAttrs"
    :class="computedClass"
  >
    <span
      v-if="loading"
      class="spinner border-2 border-current border-t-transparent rounded-full animate-spin w-4 h-4 shrink-0"
      aria-hidden="true"
    />
    <VPIcon
      v-else-if="iconPrefix"
      :icon="iconPrefix"
      aria-hidden="true"
    />
    <slot>
      {{ label }}
    </slot>
    <VPIcon
      v-if="iconSuffix"
      :icon="iconSuffix"
      aria-hidden="true"
    />
  </component>
</template>

<script lang="ts">
export type VPButtonVariant =
  | 'default'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'ghost-neutral'
  | 'destructive-primary'
  | 'destructive-secondary'
  | 'link';

// `inline` is for a button that is a word inside a sentence rather than a control beside
// one — it takes no padding and inherits the surrounding font size, so it sits on the text
// baseline instead of drawing a box around itself. Pair it with `variant="link"`.
export type VPButtonSize = 'inline' | 'sm' | 'default' | 'lg';

export type VPButtonCommonProps = {
  ariaLabel?: string;
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  // The disabled *appearance* and announcement without leaving the tab order: the element
  // keeps `aria-disabled` but stays focusable, which native `disabled` does not. For a button
  // that has to remain a focus target while being unavailable — typically one that replaced a
  // control whose focus has to land somewhere. Ignored when `disabled` or `loading` is set,
  // which already say the same thing more strongly.
  softDisabled?: boolean;
  variant?: VPButtonVariant;
  size?: VPButtonSize;
  iconPrefix?: string;
  iconSuffix?: string;
};

export type VPButtonProps =
  | (VPButtonCommonProps & { type?: 'button' | 'submit' })
  | (VPButtonCommonProps & { type: 'link'; to: RouteLocationRaw });

export default { inheritAttrs: false };

const BASE_INTERACTIVE = 'inline-flex items-center justify-center font-semibold leading-snug rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-40 disabled:pointer-events-none';

const VARIANT_CLASSES: Record<VPButtonVariant, string> = {
  default: 'bg-primary text-on-primary hover:bg-vp-primary-900',
  secondary: 'bg-vp-primary-200 text-vp-primary-900 hover:bg-vp-primary-300',
  outline: 'bg-neutral-0 text-vp-primary-900 border border-solid border-vp-primary-300 hover:bg-vp-primary-200',
  ghost: 'bg-transparent text-vp-primary-900 hover:bg-vp-primary-200 hover:text-primary',
  'ghost-neutral': 'bg-transparent text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700',
  'destructive-primary': 'bg-error text-surface hover:bg-error/90',
  'destructive-secondary': 'bg-transparent text-error border border-solid border-vp-error-200 hover:bg-vp-error-200',
  link: 'bg-transparent text-vp-primary-900 underline-offset-4 hover:underline',
};

const SIZE_CLASSES: Record<VPButtonSize, string> = {
  inline: 'gap-1 p-0 text-inherit',
  sm: 'gap-1.5 px-3 py-2 text-sm',
  default: 'gap-2 px-4 py-2.5 text-base',
  lg: 'gap-2 px-8 py-3 text-base',
};

const ICON_ONLY_SIZE_CLASSES: Record<VPButtonSize, string> = {
  // No padding either: an inline button is sized by the text around it, and an icon-only
  // one still has to sit on that baseline rather than claim a tap target of its own.
  inline: 'p-0 text-inherit',
  sm: 'p-2 text-sm',
  default: 'p-2.5 text-base',
  lg: 'p-3 text-base',
};
</script>

<script setup lang="ts">
import { computed, useAttrs, useSlots } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';
import VPIcon from './VPIcon.vue';

type VPButtonInternalProps = VPButtonCommonProps & {
  type?: 'button' | 'submit' | 'link';
  to?: RouteLocationRaw;
};

const props = withDefaults(defineProps<VPButtonInternalProps>(), {
  ariaLabel: undefined,
  label: undefined,
  loading: false,
  type: 'button',
  disabled: false,
  softDisabled: false,
  variant: undefined,
  size: undefined,
  iconPrefix: undefined,
  iconSuffix: undefined,
  to: undefined,
});

const attrs = useAttrs();
const slots = useSlots();

const computedClass = computed(() => {
  const variantSet = props.variant !== undefined;
  const sizeSet = props.size !== undefined;
  const isInteractive = variantSet || sizeSet;
  const effectiveVariant = props.variant ?? (sizeSet ? 'default' : undefined);
  const effectiveSize = props.size ?? (variantSet ? 'default' : undefined);
  const isIconOnly = !props.label && !slots.default;
  const sizeClassMap = isIconOnly ? ICON_ONLY_SIZE_CLASSES : SIZE_CLASSES;
  return [
    'vp-button',
    isInteractive ? BASE_INTERACTIVE : null,
    effectiveVariant ? VARIANT_CLASSES[effectiveVariant] : null,
    effectiveSize ? sizeClassMap[effectiveSize] : null,
    props.type === 'link' && isInteractive && (props.disabled || props.loading) ? 'pointer-events-none opacity-40' : null,
    // Spelled out rather than left to the `disabled:` variants above, which key on the native
    // attribute this deliberately does not set.
    props.softDisabled && !props.disabled && !props.loading ? 'opacity-40 pointer-events-none' : null,
  ];
});

const componentIs = computed(() => props.type === 'link' ? RouterLink : 'button');

const mergedAttrs = computed(() => {
  if (props.type === 'link') {
    return {
      ...attrs,
      to: props.to,
      'aria-disabled': (props.disabled || props.loading || props.softDisabled) ? 'true' : undefined,
      'aria-busy': props.loading ? 'true' : undefined,
      'aria-label': props.ariaLabel ?? props.label,
      tabindex: (props.disabled || props.loading) ? '-1' : undefined,
    };
  }
  return {
    ...attrs,
    type: props.type,
    disabled: props.disabled || props.loading,
    'aria-disabled': (props.disabled || props.loading || props.softDisabled) ? 'true' : undefined,
    'aria-busy': props.loading ? 'true' : 'false',
    'aria-label': props.ariaLabel ?? props.label,
  };
});
</script>
